// V2.12 — Recent testimonial card (landing + QuoteView pre-sign).
// Fetches GET /public-stats/recent-review (204 = hide component).
// - Star rating (Lucide Star, filled from 0..5)
// - Rounded emerald avatar with initials
// - Click → /reviews page (full list)
// - Server sends `time_ago = {unit, value}` for i18n localisation here.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Star } from "lucide-react";
import { api } from "@/lib/api";

function initials(name) {
  return (name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0].toUpperCase())
    .join("");
}

function useTimeAgoLabel(timeAgo) {
  const { t } = useTranslation();
  if (!timeAgo) return "";
  const { unit, value } = timeAgo;
  if (unit === "today") return t("landing.recent_testimonial.time_ago.today");
  return t(`landing.recent_testimonial.time_ago.${unit}`, { count: value, defaultValue: `${value} ${unit}` });
}

export default function RecentTestimonialCard({ className = "" }) {
  const { t } = useTranslation();
  const [review, setReview] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, status } = await api.get("/public-stats/recent-review", {
          validateStatus: (s) => s === 200 || s === 204,
        });
        if (cancelled) return;
        if (status === 204 || !data) {
          setHidden(true);
        } else {
          setReview(data);
        }
      } catch {
        if (!cancelled) setHidden(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const timeAgoLabel = useTimeAgoLabel(review?.time_ago);

  if (hidden) return null;
  if (!review) {
    return (
      <div
        data-testid="recent-testimonial-skeleton"
        className={`mx-auto max-w-md animate-pulse rounded-xl border border-slate-200 bg-white p-4 shadow-sm ${className}`}
      >
        <div className="h-3 w-24 rounded bg-slate-100" />
        <div className="mt-3 h-3 w-full rounded bg-slate-100" />
        <div className="mt-1.5 h-3 w-5/6 rounded bg-slate-100" />
      </div>
    );
  }

  return (
    <Link
      to="/reviews"
      data-testid="recent-testimonial-card"
      className={`group mx-auto block max-w-md rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition-all hover:border-emerald-300 hover:shadow-md ${className}`}
    >
      <div className="flex items-start gap-3">
        <span className="grid h-10 w-10 flex-none place-items-center rounded-full bg-emerald-100 text-sm font-semibold text-emerald-700">
          {initials(review.author)}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1" data-testid="recent-testimonial-stars">
            {[1, 2, 3, 4, 5].map((i) => (
              <Star
                key={i}
                className={`h-3.5 w-3.5 ${i <= review.rating ? "fill-amber-400 text-amber-400" : "text-slate-200"}`}
                strokeWidth={1.5}
              />
            ))}
            <span className="ml-1 text-[10px] uppercase tracking-widest text-slate-400">
              {t("landing.recent_testimonial.verified")}
            </span>
          </div>
          <p
            data-testid="recent-testimonial-body"
            className="mt-1.5 text-sm leading-relaxed text-slate-700"
          >
            « {review.body_excerpt} »
          </p>
          <p className="mt-2 text-xs text-slate-500">
            — <span className="font-medium text-slate-700">{review.author}</span>
            {review.city && <>, {review.city}</>}
            {timeAgoLabel && <> · {timeAgoLabel}</>}
          </p>
        </div>
      </div>
      <span className="mt-3 block text-right text-xs font-medium text-emerald-600 opacity-0 transition-opacity group-hover:opacity-100">
        {t("landing.recent_testimonial.see_all")} →
      </span>
    </Link>
  );
}
